/**
 * InheritedField — wraps a form field with inheritance indicators.
 *
 * Shows:
 * - Coral left-border + bold label when overridden at current tier
 * - Dashed left-border + dim label + "from: System/Project" chip when inherited
 * - Toggle to customize (override) or reset (clear override)
 */

import { useState } from 'react';
import { X, Pencil } from 'lucide-react';

export type TierSource = 'system' | 'workspace' | 'project' | 'sprint';

interface InheritedFieldProps {
  label: string;
  source: TierSource;
  isOverridden: boolean;
  inheritedValue?: string;
  onClearOverride?: () => void;
  children: React.ReactNode;
  className?: string;
}

const SOURCE_LABELS: Record<TierSource, string> = {
  system: 'System',
  workspace: 'Workspace',
  project: 'Project',
  sprint: 'Sprint',
};

const SOURCE_COLORS: Record<TierSource, string> = {
  system: 'bg-neutral-100 text-neutral-500',
  workspace: 'bg-neutral-100 text-neutral-500',
  project: 'bg-coral-50 text-coral-600',
  sprint: 'bg-amber-50 text-amber-700',
};

export function InheritedField({
  label,
  source,
  isOverridden,
  inheritedValue,
  onClearOverride,
  children,
  className = '',
}: InheritedFieldProps) {
  const [showOverride, setShowOverride] = useState(isOverridden);

  const handleStartOverride = () => setShowOverride(true);

  const handleClearOverride = () => {
    setShowOverride(false);
    onClearOverride?.();
  };

  if (!isOverridden && !showOverride) {
    // Inherited state — show dim value with source chip
    return (
      <div className={`group relative pl-3 border-l-2 border-dashed border-neutral-200 ${className}`}>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[11px] font-medium text-neutral-400 uppercase tracking-wider font-mono">
            {label}
          </label>
          <div className="flex items-center gap-1.5">
            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${SOURCE_COLORS[source]}`}>
              from: {SOURCE_LABELS[source]}
            </span>
            <button
              onClick={handleStartOverride}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-400 hover:text-coral-500 p-0.5"
              title="Customize for this tier"
            >
              <Pencil size={10} />
            </button>
          </div>
        </div>
        {inheritedValue !== undefined && (
          <div className="text-[12px] text-neutral-400 font-mono">
            {inheritedValue}
          </div>
        )}
      </div>
    );
  }

  // Overridden state — show full editor with coral accent
  return (
    <div className={`relative pl-3 border-l-2 border-solid border-coral-400 ${className}`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] font-semibold text-neutral-700 uppercase tracking-wider font-mono">
          {label}
        </label>
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-coral-50 text-coral-600">
            overridden
          </span>
          {onClearOverride && (
            <button
              onClick={handleClearOverride}
              className="text-neutral-400 hover:text-red-500 transition-colors p-0.5"
              title="Reset to inherited value"
            >
              <X size={10} />
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

/**
 * FieldGroup — groups related fields with a section header.
 * Mirrors the CollapsibleSection from DevSettingsForm but designed for the tier layout.
 */
interface FieldGroupProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function FieldGroup({ title, children, defaultOpen = true }: FieldGroupProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-neutral-200/60 bg-white overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-neutral-50/80 hover:bg-neutral-100/60 transition-colors"
      >
        <h4 className="text-[11px] font-bold text-neutral-600 uppercase tracking-[0.1em] font-sans">
          {title}
        </h4>
        <svg
          className={`w-3.5 h-3.5 text-neutral-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="p-4 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
}
